import React, { useState } from 'react';
import {
  StyleSheet,
  View,
  ScrollView,
  ImageBackground,
  Image,
  TouchableOpacity,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/core';
import Swiper from 'react-native-web-swiper';
import { Color, CommonProperties, CommonStyles, defaultFontSize } from '../../../styles';
import { scale } from '../../../utils/scale.utils';
import Avatar from '../../Avatar';
import BaseText from '../../BaseText';
import Button, { BUTTON_TYPES } from '../../Button';
import Icon from '../../Icon';
import RowView from '../../RowView';
import EventSection from '../../TripHub/Event/EventSection';
import WrapBottomColor from '../../containers/WrapBottomColor';
import BottomButton from '../../BottomButton';
import { LinearGradient } from 'expo-linear-gradient';
import MapView from 'react-native-maps';
import moment from 'moment';
import Images from '../../../constants/Images';
import SpeakerCard from '../../TripEvents/SpeakerCard';
import { Modals, Screens } from '../../../constants/ScreenNames';
import EventCancelModal from '../../TripHub/Event/EventCancelModal';
import EventNextStep from '../../TripHub/Event/EventNextStep';

const SectionTitle = ({ label }) => {
  return (
    <RowView style={CommonStyles.justifyBetween}>
      <BaseText style={[CommonStyles.h2, CommonStyles.mb2]}>{label}</BaseText>
    </RowView>
  );
};

const AddressSection = ({ address, style }) => {
  return (
    <RowView style={[addressStyles.container, style]}>
      <Icon name="mark" style={addressStyles.icon} />
      <BaseText style={addressStyles.text}>{address}</BaseText>
    </RowView>
  );
};

const addressStyles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  icon: {
    color: Color.primary,
    fontSize: defaultFontSize.h1 * 2.2,
    ...CommonProperties.mr3,
  },
  text: {
    color: Color.primary,
    ...CommonProperties.h4,
    maxWidth: 160,
  },
});

const EventPreviewModal = () => {
  const navigation = useNavigation();
  const [showMap, setShowMap] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const route = useRoute();
  const { event, speakers, user, meetings, description } = route?.params || {};

  const onClose = () => {
    setShowOptions(false);
  };

  const onShowOptions = () => {
    setShowOptions(true);
  };
  const onSubmit = () => {
    navigation.navigate(Modals.InvitationTravelerModal);
  };
  const onCancel = () => {
    setShowOptions(false);
    setShowCancelPopup(true);
  };

  const onEditEvent = () => {
    navigation.navigate(Modals.EventEditModal, {
      event,
      speakers,
      meetings,
      description,
      returnScreen: route.name,
    });
  };

  return (
    <WrapBottomColor style={styles.container}>
      <EventCancelModal
        visible={showCancelPopup}
        onSubmit={() => navigation.navigate(Modals.InvitationTravelerModal)}
        onClose={() => setShowCancelPopup(false)}
        onCancel={() => {
          setShowCancelPopup(false);
          navigation.navigate(Screens.Home);
        }}
      />
      <EventNextStep
        visible={showOptions}
        onSaveEvent={onSubmit}
        onSaveBook={onSubmit}
        onSubmit={onSubmit}
        onCancel={onCancel}
        onClose={onClose}
      />
      <RowView style={styles.header}>
        <View style={CommonStyles.flex1}>
          <BaseText numberOfLines={1} style={[CommonStyles.h1, CommonStyles.pr3]}>
            {event?.city?.displaylabel}
          </BaseText>
          <BaseText style={CommonStyles.h4}>
            {event?.startDate && event?.endDate
              ? `${moment(event.startDate).format('MMM DD')} - ${moment(event.endDate).format(
                  'MMM DD'
                )}`
              : null}
          </BaseText>
        </View>
        <Button
          onPress={onEditEvent}
          style={styles.editButton}
          label="Edit"
          labelStyle={CommonStyles.h3}
        />
      </RowView>
      <ScrollView style={CommonStyles.pb5}>
        <ImageBackground
          style={styles.bckImage}
          source={event?.eventImage ? { uri: event?.eventImage } : null}
        >
          <LinearGradient colors={['rgba(0,0,0,.7)', 'transparent']} style={styles.linearGradient}>
            <Avatar
              style={styles.avatar}
              source={{
                uri: user?.url,
              }}
              size={140}
            />
            <BaseText style={styles.eventName}>{event?.name}</BaseText>
            <View style={styles.imageButton}>
              <Icon name="image" color={Color.primary} size={32} />
            </View>
          </LinearGradient>
        </ImageBackground>
        <View style={styles.body}>
          <BaseText style={styles.title}>{`${user?.name}`} is inviting you</BaseText>
          <BaseText style={CommonStyles.h2}>Description</BaseText>
          <BaseText style={[CommonStyles.h4, CommonStyles.mt3]}>{description}</BaseText>
          {!showMap ? (
            <Image
              style={[styles.venueImage, CommonStyles.borderRadius2, CommonStyles.mt5]}
              source={event?.venueImage ? { uri: event?.venueImage } : Images.default}
            />
          ) : (
            <MapView
              style={styles.mapStyle}
              initialRegion={{
                latitude: 32.89748,
                longitude: -97.040443,
                latitudeDelta: 0.0922,
                longitudeDelta: 0.0421,
              }}
            />
          )}
          <View style={CommonStyles.justifyCenter}>
            <Button
              type={BUTTON_TYPES.TEXT}
              label={!showMap ? 'View Map' : 'View Image'}
              labelStyle={CommonStyles.h3}
              onPress={() => setShowMap(!showMap)}
            />
          </View>
          {event?.venue ? (
            <TouchableOpacity>
              <AddressSection address={event?.venue?.displaylabel} style={CommonStyles.my5} />
            </TouchableOpacity>
          ) : null}
          {event?.virtualLink ? (
            <View style={[CommonStyles.px3, CommonStyles.py5]}>
              <BaseText style={[CommonStyles.h2, CommonStyles.mb3]}>
                Virtual Link Available
              </BaseText>
              <BaseText style={[CommonStyles.h3, { color: Color.primary }]}>
                {event?.virtualLink}
              </BaseText>
            </View>
          ) : null}
        </View>
        {speakers ? (
          <View style={styles.speakerSection}>
            <SectionTitle
              label="Speaker Lineup"
              buttonLabel="Add Speaker"
              onPress={() =>
                navigation.push(Modals.EventSettingsModal, {
                  disableMeetings: true,
                  disableVenue: true,
                })
              }
            />
            <Swiper
              containerStyle={{ minHeight: scale(240) }}
              controlsProps={{
                cellsContent: {},
                prevTitle: '',
                nextTitle: '',
                dotsWrapperStyle: { display: 'none' },
              }}
              minDistanceForAction={0.1}
            >
              {Object.values(speakers).map((speaker) => (
                <SpeakerCard key={speaker.id} speaker={speaker} />
              ))}
            </Swiper>
          </View>
        ) : null}
        <View style={[CommonStyles.px3, CommonStyles.py5, CommonStyles.mt5]}>
          <SectionTitle
            label="Schedule"
            buttonLabel="Add Meeting"
            onPress={() =>
              navigation.push(Modals.EventSettingsModal, {
                disableSpeakers: true,
                disableVenue: true,
              })
            }
          />
          <EventSection meetings={meetings ? meetings : []} />
        </View>
      </ScrollView>
      <BottomButton
        stlye={CommonStyles.py4}
        labelStyle={{ ...CommonProperties.py2, ...CommonProperties.h2 }}
        label="Continue"
        full
        onPress={onShowOptions}
      />
    </WrapBottomColor>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Color.accent,
  },
  title: {
    ...CommonProperties.h1,
    textAlign: 'center',
    ...CommonProperties.mb5,
  },
  header: {
    justifyContent: 'space-between',
    alignItems: 'center',
    ...CommonProperties.px4,
    ...CommonProperties.py4,
  },
  body: {
    flex: 1,
    marginTop: 42,
    ...CommonProperties.p4,
  },
  editButton: {
    paddingHorizontal: 42,
    //...CommonProperties.px5,
    ...CommonProperties.py3,
    ...CommonProperties.shadow,
    ...CommonProperties.borderRadius4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bckImage: {
    width: '100%',
    height: scale(200),
    resizeMode: 'cover',
  },
  venueImage: {
    width: '100%',
    height: scale(200),
    resizeMode: 'cover',
    ...CommonProperties.borderRadius2,
    ...CommonProperties.mt5,
  },
  mapStyle: {
    width: '100%',
    height: scale(200),
    ...CommonProperties.borderRadius3,
    ...CommonProperties.mt5,
    overflow: 'hidden',
  },
  avatar: {
    marginBottom: -42,
  },
  linearGradient: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    //justifyContent: 'center',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  eventName: {
    position: 'absolute',
    top: scale(12),
    color: Color.accent,
    ...CommonProperties.h2,
    textAlign: 'center',
    width: '80%',
  },
  imageButton: {
    bottom: 12,
    right: 12,
    position: 'absolute',
    backgroundColor: Color.accent,
    width: scale(56),
    height: scale(56),
    borderRadius: scale(28),
    borderColor: Color.primary,
    borderWidth: 0.5,
    justifyContent: 'center',
    alignItems: 'center',
  },
  speakerSection: {
    backgroundColor: '#efefef', // TODO: find where we can reuse this color
    ...CommonProperties.py3,
    ...CommonProperties.px4,
  },
});

export default EventPreviewModal;
